import { Link } from "react-router-dom";
import { ArrowRight, Heart, HandHeart, Sparkles } from "lucide-react";
import { FOUNDATION } from "@/data/content";
import { useLocale } from "@/context/LocaleContext";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/api";

export default function Hero() {
  const { t } = useLocale();
  const { settings } = useSettings();
  const h = settings?.hero || {};
  const f = settings?.foundation || {};

  const title1 = h.title_line1 || t("hero.title1");
  const titleItalic = h.title_italic || t("hero.title2");
  const title2 = h.title_line2 || t("hero.title3");
  const titleAccent = h.title_accent || t("hero.title4");
  const desc = h.description || t("hero.desc");
  const badge = h.badge_text || t("hero.badge");
  const ctaDonate = h.cta_donate_label || t("hero.cta_donate");
  const ctaDonateUrl = h.cta_donate_url || "/donasi";
  const ctaVol = h.cta_volunteer_label || t("hero.cta_volunteer");
  const ctaVolUrl = h.cta_volunteer_url || "/volunteer";
  const ctaLearn = h.cta_learn_label || t("hero.cta_learn");
  const ctaLearnUrl = h.cta_learn_url || "/tentang";
  const heroImage = h.image_url || "https://images.unsplash.com/photo-1533734635438-fa92e72a1f0e?w=900&q=80";
  const statLabel = h.stat_label || t("hero.card_impact");
  const statValue = h.stat_value || t("hero.card_title");
  const statDesc = h.stat_desc || t("hero.card_desc");
  const badgeLive = h.badge_live_text || t("hero.badge_live");
  const tagline = f.tagline_sundanese || FOUNDATION.taglineSundanese;
  const taglineTranslation = f.tagline_translation || FOUNDATION.taglineTranslation;

  return (
    <section className="relative overflow-hidden bg-[#F7F5F0]" data-testid="hero-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 80% 10%, rgba(242,140,31,.10) 0%, transparent 60%), radial-gradient(900px 500px at 10% 90%, rgba(27,58,142,.08) 0%, transparent 60%)" }} />
      <div className="grain-overlay" />
      <div className="pointer-events-none absolute -top-10 right-[8%] hero-shape"><div className="h-24 w-24 rounded-full bg-[var(--brand-orange)]/20 blur-2xl" /></div>
      <div className="pointer-events-none absolute top-1/3 left-[6%] hero-shape delayed"><div className="h-32 w-14 rounded-full bg-[var(--brand-blue)]/25 blur-2xl" /></div>
      <div className="pointer-events-none absolute bottom-24 right-[16%] hero-shape delayed-2"><div className="h-16 w-16 rounded-full bg-[var(--brand-orange)]/30 blur-xl" /></div>

      <div className="container-sf relative py-24 md:py-32 lg:py-40">
        <div className="grid lg:grid-cols-[1.15fr,0.85fr] gap-14 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-slate-200/70 px-4 py-1.5 mb-8">
              <Sparkles className="h-3.5 w-3.5 text-[var(--brand-orange)]" />
              <span className="text-xs md:text-sm font-semibold text-slate-700 tracking-wide">{badge}</span>
            </div>

            <h1 className="text-[2.6rem] sm:text-6xl lg:text-[4.6rem] font-bold leading-[1.02] tracking-[-0.03em] text-slate-900">
              {title1}{" "}<span className="font-serif-display italic font-normal text-[var(--brand-blue)]">{titleItalic}</span><br className="hidden sm:block" /> {title2}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[var(--brand-orange)]">{titleAccent}</span>
                <span className="absolute inset-x-0 bottom-1 h-2.5 bg-[var(--brand-orange)]/20 -z-0" />
              </span>.
            </h1>

            <p className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">{desc}</p>
            <p className="mt-5 font-serif-display italic text-lg text-[var(--brand-blue)]">&ldquo;{tagline}&rdquo; &middot; {taglineTranslation}</p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={ctaDonateUrl} className="btn-primary" data-testid="hero-donate-cta"><Heart className="h-4 w-4" /> {ctaDonate}</Link>
              <Link to={ctaVolUrl} className="btn-outline" data-testid="hero-volunteer-cta"><HandHeart className="h-4 w-4" /> {ctaVol}</Link>
              <Link to={ctaLearnUrl} className="btn-ghost group" data-testid="hero-learn-more">{ctaLearn} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
              {FOUNDATION.coreFocus.map((f, i) => (
                <div key={f} className="border-l-2 border-slate-200 pl-4">
                  <div className="marker-num text-3xl">0{i + 1}</div>
                  <div className="mt-1 text-sm font-semibold text-slate-700">{f}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative aspect-[4/5] w-full max-w-md ml-auto">
              <div className="absolute -top-8 -right-8 h-72 w-72 rounded-full bg-[var(--brand-blue)]/95" />
              <div className="absolute top-8 right-8 h-40 w-40 rounded-full bg-[var(--brand-orange)]" />
              <div className="absolute inset-0 rounded-[28px] overflow-hidden shadow-2xl bg-white p-3">
                <div className="w-full h-full rounded-[20px] overflow-hidden bg-slate-100">
                  <img src={getImageUrl(heroImage)} alt="Sauyunan" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-8 rounded-2xl bg-white shadow-xl border border-slate-100 px-6 py-5 max-w-[220px]">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[var(--brand-orange)]/15 flex items-center justify-center"><Heart className="h-5 w-5 text-[var(--brand-orange)]" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{statLabel}</div>
                    <div className="text-lg font-bold text-slate-900">{statValue}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-600 leading-relaxed">{statDesc}</div>
              </div>
              <div className="absolute -top-4 left-8 rounded-full bg-white shadow-lg border border-slate-100 px-4 py-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-semibold text-slate-700">{badgeLive}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="h-16 bg-gradient-to-b from-transparent to-[var(--bg)] relative" />
    </section>
  );
}
